class BMProduct extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-bm-variants]');
    if (!json) return;
    try { this.variants = JSON.parse(json.textContent); } catch (_) { return; }
    this.form = this.querySelector('form[action*="/cart/add"]');
    this.idInput = this.querySelector('input[name="id"]');
    this.price = this.querySelector('[data-bm-price]');
    this.compare = this.querySelector('[data-bm-compare]');
    this.sale = this.querySelector('[data-bm-sale]');
    this.submit = this.querySelector('[data-bm-submit]');
    this.sticky = this.parentElement.querySelector('[data-bm-sticky]');
    this.stickyButton = this.sticky?.querySelector('button');
    this.gallery = this.querySelector('[data-bm-product-gallery]');
    this.gallerySlides = this.gallery ? [...this.gallery.querySelectorAll('.bm-product__media')] : [];
    this.galleryCurrent = this.querySelector('[data-bm-gallery-current]');
    this.galleryIndex = 0;
    this.querySelectorAll('[data-bm-option]').forEach((button) => {
      button.addEventListener('click', () => this.selectOption(button));
    });
    this.form?.addEventListener('submit', () => {
      this.submit?.setAttribute('aria-busy', 'true');
      if (this.submit) this.submit.textContent = 'Adding…';
      if (this.stickyButton) { this.stickyButton.textContent = 'Adding…'; this.stickyButton.disabled = true; }
    });
    this.sticky?.querySelector('button')?.addEventListener('click', () => this.submit?.click());
    this.querySelector('[data-bm-gallery-prev]')?.addEventListener('click', () => this.moveGallery(-1));
    this.querySelector('[data-bm-gallery-next]')?.addEventListener('click', () => this.moveGallery(1));
    this.gallery?.addEventListener('scroll', () => {
      window.requestAnimationFrame(() => this.syncGalleryIndex());
    }, { passive: true });
    if (this.submit && this.sticky) {
      const observer = new IntersectionObserver(([entry]) => this.sticky.classList.toggle('is-visible', !entry.isIntersecting && (this.classList.contains('bm-product--clean') || !this.classList.contains('bm-product--butter-it') || entry.boundingClientRect.bottom < 0)), { threshold: .2 });
      observer.observe(this.submit);
    }
    this.updateAvailability();
    const initialVariant = this.variants.find((variant) => String(variant.id) === this.idInput?.value);
    if (initialVariant) this.updateVariant(initialVariant);
    const demo = this.querySelector('[data-bm-buy-demo]');
    if (demo && !window.matchMedia('(prefers-reduced-motion: reduce)').matches && 'IntersectionObserver' in window) {
      this.demoObserver = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) { demo.muted = true; demo.play().catch(() => {}); }
        else demo.pause();
      }, { threshold: .25 });
      this.demoObserver.observe(demo);
    }
  }

  selectOption(button) {
    const group = button.closest('[data-bm-option-group]');
    group?.querySelectorAll('[data-bm-option]').forEach((item) => item.setAttribute('aria-pressed', 'false'));
    button.setAttribute('aria-pressed', 'true');
    const selectedLabel = group?.querySelector('[data-bm-selected]');
    if (selectedLabel) selectedLabel.textContent = button.dataset.value;
    const selected = [...this.querySelectorAll('[data-bm-option][aria-pressed="true"]')].map((item) => item.dataset.value);
    const position = Number(button.dataset.position) - 1;
    const variant = this.variants.find((item) => item.options.every((value, index) => value === selected[index]))
      || this.variants.find((item) => item.available && item.options[position] === button.dataset.value);
    if (!variant) return;
    this.querySelectorAll('[data-bm-option-group]').forEach((optionGroup, index) => {
      optionGroup.querySelectorAll('[data-bm-option]').forEach((item) => item.setAttribute('aria-pressed', String(item.dataset.value === variant.options[index])));
      const label = optionGroup.querySelector('[data-bm-selected]');
      if (label) label.textContent = variant.options[index];
    });
    this.idInput.value = variant.id;
    this.updateVariant(variant);
    this.updateAvailability();
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  moveGallery(direction) {
    if (!this.gallerySlides.length) return;
    const nextIndex = (this.galleryIndex + direction + this.gallerySlides.length) % this.gallerySlides.length;
    this.gallery?.scrollTo({ left: this.gallerySlides[nextIndex].offsetLeft, behavior: 'smooth' });
    this.galleryIndex = nextIndex;
    this.updateGalleryCount();
  }

  syncGalleryIndex() {
    if (!this.gallery || !this.gallerySlides.length) return;
    const galleryLeft = this.gallery.getBoundingClientRect().left;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    this.gallerySlides.forEach((slide, index) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - galleryLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    this.galleryIndex = closestIndex;
    this.updateGalleryCount();
  }

  updateGalleryCount() {
    if (this.galleryCurrent) this.galleryCurrent.textContent = String(this.galleryIndex + 1).padStart(2, '0');
  }

  updateVariant(variant) {
    if (this.price) this.price.textContent = this.formatMoney(variant.price);
    if (this.compare) {
      this.compare.textContent = variant.compare_at_price > variant.price ? this.formatMoney(variant.compare_at_price) : '';
      this.compare.hidden = !(variant.compare_at_price > variant.price);
    }
    if (this.sale) this.sale.hidden = !(variant.compare_at_price > variant.price);
    if (this.submit) {
      this.submit.disabled = !variant.available;
      this.submit.textContent = variant.available ? 'Add to cart' : 'Sold out';
      this.submit.removeAttribute('aria-busy');
    }
    if (this.stickyButton) {
      this.stickyButton.disabled = !variant.available;
      this.stickyButton.textContent = variant.available ? 'Add to cart' : 'Sold out';
    }
    const stickyVariant = this.sticky?.querySelector('[data-bm-sticky-variant]');
    if (stickyVariant) stickyVariant.textContent = variant.title === 'Default Title' ? '' : variant.title;
    const design = this.querySelector('[data-bm-design]');
    if (design) design.textContent = variant.option1;
    const photoNote = this.querySelector('[data-bm-design-photo]');
    if (photoNote) {
      photoNote.hidden = variant.option1 === 'Clockwise';
      const photoName = photoNote.querySelector('[data-bm-design-photo-name]');
      if (photoName) photoName.textContent = variant.option1;
    }
    const stickyPrice = this.sticky?.querySelector('[data-bm-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = this.formatMoney(variant.price);
    const target = this.querySelector(`[data-media-id="${variant.featured_media?.id}"]`);
    if (target && this.gallery) this.gallery.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
  }

  updateAvailability() {
    const selected = [...this.querySelectorAll('[data-bm-option][aria-pressed="true"]')].map((item) => item.dataset.value);
    this.querySelectorAll('[data-bm-option]').forEach((button) => {
      const position = Number(button.dataset.position) - 1;
      const possible = [...selected];
      possible[position] = button.dataset.value;
      const exists = this.variants.some((variant) => variant.available && variant.options.every((value, index) => value === possible[index] || index > position));
      button.disabled = !exists && button.getAttribute('aria-pressed') !== 'true';
      button.classList.toggle('is-unavailable', !exists);
    });
  }

  formatMoney(cents) {
    const currency = this.dataset.currency || 'USD';
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency, currencyDisplay: 'code' }).format(cents / 100); }
    catch (_) { return `$${(cents / 100).toFixed(2)} USD`; }
  }
}

if (!customElements.get('bm-product')) customElements.define('bm-product', BMProduct);

document.documentElement.classList.add('bm-js');

const bmRevealItems = document.querySelectorAll('[data-bm-reveal]');
if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  const bmRevealObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('bm-revealed');
      observer.unobserve(entry.target);
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -8% 0px' });
  bmRevealItems.forEach((item) => bmRevealObserver.observe(item));
} else {
  bmRevealItems.forEach((item) => item.classList.add('bm-revealed'));
}

const bmReviewTrack = document.querySelector('[data-bm-review-track]');
if (bmReviewTrack) {
  const scrollReviews = (direction) => {
    const card = bmReviewTrack.querySelector('.bm2-review-card');
    const gap = Number.parseFloat(window.getComputedStyle(bmReviewTrack).columnGap) || 16;
    const distance = card ? card.getBoundingClientRect().width + gap : 400;
    bmReviewTrack.scrollBy({ left: distance * direction, behavior: 'smooth' });
  };
  document.querySelector('[data-bm-review-prev]')?.addEventListener('click', () => scrollReviews(-1));
  document.querySelector('[data-bm-review-next]')?.addEventListener('click', () => scrollReviews(1));
  bmReviewTrack.addEventListener('keydown', (event) => {
    if (event.target !== bmReviewTrack || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    scrollReviews(event.key === 'ArrowRight' ? 1 : -1);
  });
}

const bmWrittenReviewTrack = document.querySelector('[data-bm-written-track]');
if (bmWrittenReviewTrack) {
  const scrollWrittenReviews = (direction) => {
    const card = bmWrittenReviewTrack.querySelector('.bm-written-review');
    const gap = Number.parseFloat(window.getComputedStyle(bmWrittenReviewTrack).columnGap) || 16;
    const distance = card ? card.getBoundingClientRect().width + gap : 400;
    bmWrittenReviewTrack.scrollBy({ left: distance * direction, behavior: 'smooth' });
  };
  document.querySelector('[data-bm-written-prev]')?.addEventListener('click', () => scrollWrittenReviews(-1));
  document.querySelector('[data-bm-written-next]')?.addEventListener('click', () => scrollWrittenReviews(1));
  bmWrittenReviewTrack.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    scrollWrittenReviews(event.key === 'ArrowRight' ? 1 : -1);
  });
}
